import { useState, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { createCheckoutSession } from '../services/stripe';
import { setPendingCheckout } from '../utils/pendingCheckout';
import { openStripePortal } from '../lib/billing';

export const useCheckout = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useCheckout must be used within AuthProvider');
  }
  const { user, session } = context;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startCheckout = async (priceId: string) => {
    if (!user || !session?.access_token) {
      // Store checkout intent in localStorage with timestamp (2-minute expiration)
      setPendingCheckout(priceId);

      // Trigger UserModal to open via custom event
      window.dispatchEvent(new CustomEvent('open-user-modal-for-checkout'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { url } = await createCheckoutSession(priceId, session.access_token);
      window.location.href = url; // Redirect to Stripe Checkout
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  const openCustomerPortal = async () => {
    if (!user || !session?.access_token) {
      setError('You must be logged in to manage subscriptions');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await openStripePortal(`${window.location.origin}/settings/subscription`);
    } catch (err) {
      console.error('Portal error:', err);
      setError(err instanceof Error ? err.message : 'Failed to open customer portal');
    } finally {
      setLoading(false);
    }
  };

  return { startCheckout, openCustomerPortal, loading, error };
};
